import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/axios';
import {
  Send, Loader2, Plus, MessageSquare, Trash2,
  BarChart3, Factory, ShoppingCart, Target,
  Ship, Zap, ChevronRight, Sparkles, Anchor,
  Paperclip, X, Headset, LifeBuoy, Inbox, RefreshCw, User as UserIcon,
  ThumbsUp, ThumbsDown,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { TitleWithHelp } from '../components/ui/help-tooltip';

const TIPOS_CHAMADO = [
  { valor: 'duvida', label: 'Dúvida' },
  { valor: 'erro', label: 'Erro' },
  { valor: 'melhoria', label: 'Melhoria' },
  { valor: 'analise_informacao', label: 'Análise de Informação' },
  { valor: 'nova_funcionalidade', label: 'Nova Funcionalidade' },
];

// Menu Inicial (Quick Replies): opções de triagem exibidas sempre que uma conversa nova abre.
// "Analisar informação" força Modo Negócio (BI); as outras 3 forçam Modo Suporte.
const RESPOSTAS_RAPIDAS = [
  { emoji: '🚩', label: 'Encontrei um erro', texto: 'Encontrei um erro no sistema.', forcarSuporte: true },
  { emoji: '❓', label: 'Dúvida em funcionalidade', texto: 'Tenho uma dúvida sobre uma funcionalidade do sistema.', forcarSuporte: true },
  { emoji: '💡', label: 'Sugerir melhoria', texto: 'Quero sugerir uma melhoria para o sistema.', forcarSuporte: true },
  { emoji: '📊', label: 'Analisar informação', texto: 'Quero analisar informações do sistema: lucros, vendas e cotações.', forcarNegocio: true },
];

const TIPO_LABEL = { duvida: 'Dúvida', erro: 'Erro', melhoria: 'Melhoria', analise_informacao: 'Análise de Informação', nova_funcionalidade: 'Nova Funcionalidade' };
const TIPO_COLOR = {
  duvida: 'bg-blue-100 text-blue-700 border-blue-200',
  erro: 'bg-red-100 text-red-700 border-red-200',
  melhoria: 'bg-purple-100 text-purple-700 border-purple-200',
  analise_informacao: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  nova_funcionalidade: 'bg-teal-100 text-teal-700 border-teal-200',
};
const STATUS_LABEL = { aberto: 'Aberto', em_analise: 'Em Análise', pendente_usuario: 'Pendente Usuário', resolvido: 'Resolvido' };
const STATUS_COLOR = {
  aberto: 'bg-amber-100 text-amber-700 border-amber-200',
  em_analise: 'bg-blue-100 text-blue-700 border-blue-200',
  pendente_usuario: 'bg-purple-100 text-purple-700 border-purple-200',
  resolvido: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};
const PRIORIDADE_LABEL = { baixa: 'Baixa', media: 'Média', alta: 'Alta', urgente: 'Urgente' };
const PRIORIDADE_COLOR = {
  baixa: 'bg-gray-100 text-gray-600 border-gray-200',
  media: 'bg-blue-100 text-blue-700 border-blue-200',
  alta: 'bg-orange-100 text-orange-700 border-orange-200',
  urgente: 'bg-red-100 text-red-700 border-red-200',
};

const PROMPT_SUGGESTIONS = [
  {
    area: 'Comercial',
    icon: Target,
    color: 'text-blue-700 bg-blue-50 border-blue-200',
    prompts: [
      'Analise meu pipeline de vendas e sugira ações para acelerar conversões',
      'Quais propostas estão sem retorno há mais de 10 dias?',
      'Gere um texto de follow-up para um cliente que pediu proposta há 1 semana',
      'Qual o ticket médio das propostas deste mês?',
    ]
  },
  {
    area: 'Produção',
    icon: Factory,
    color: 'text-orange-600 bg-orange-50 border-orange-200',
    prompts: [
      'Identifique possíveis gargalos nos projetos em andamento',
      'Sugira otimizações para o cronograma de produção atual',
      'Quais projetos estão com maior risco de atraso?',
      'Qual o progresso geral das embarcações em produção?',
    ]
  },
  {
    area: 'Compras',
    icon: ShoppingCart,
    color: 'text-green-600 bg-green-50 border-green-200',
    prompts: [
      'Analise os fornecedores cadastrados e sugira melhorias',
      'Quais pedidos de compra estão pendentes?',
      'Sugira estratégias de negociação para reduzir custos de insumos',
      'Há produtos com estoque abaixo do mínimo?',
    ]
  },
  {
    area: 'Gestão',
    icon: BarChart3,
    color: 'text-purple-600 bg-purple-50 border-purple-200',
    prompts: [
      'Faça um resumo executivo completo da situação atual da empresa',
      'Quais KPIs devo acompanhar semanalmente?',
      'Analise a performance geral e sugira melhorias prioritárias',
      'Liste as tarefas pendentes e suas prioridades',
    ]
  },
];

export default function NauCopilot() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState('chat'); // 'chat' | 'chamados'
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [model, setModel] = useState('flash');
  const [imagemBase64, setImagemBase64] = useState(null);
  const [chamadoDialogOpen, setChamadoDialogOpen] = useState(false);
  const [chamadoTipo, setChamadoTipo] = useState('duvida');
  const [chamadoContexto, setChamadoContexto] = useState(null); // { msgId, mensagem, titulo, anexos }
  const [criandoChamado, setCriandoChamado] = useState(false);
  const [chamadosAbertos, setChamadosAbertos] = useState({}); // { [msgId]: true }
  const [feedbackDado, setFeedbackDado] = useState({}); // { [msgId]: 'positivo'|'negativo' }
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const usuarioLogado = JSON.parse(localStorage.getItem('nautilus_user') || '{}');
  const primeiroNome = (usuarioLogado.full_name || '').split(' ')[0] || 'visitante';

  // Meus Chamados
  const [meusChamados, setMeusChamados] = useState([]);
  const [loadingChamados, setLoadingChamados] = useState(false);
  const [chamadoSelecionado, setChamadoSelecionado] = useState(null);
  const [respostaChamado, setRespostaChamado] = useState('');
  const [anexoChamado, setAnexoChamado] = useState(null); // { file, preview }
  const [enviandoResposta, setEnviandoResposta] = useState(false);
  const historicoEndRef = useRef(null);
  const fileInputChamadoRef = useRef(null);

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => { historicoEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chamadoSelecionado]);

  // Deep-link: /nau?chamado_id=X abre direto na aba "Meus Chamados" já selecionado.
  // /nau?view=chamados abre na aba "Meus Chamados" (sem selecionar nenhum em particular).
  useEffect(() => {
    const chamadoIdParam = searchParams.get('chamado_id');
    const viewParam = searchParams.get('view');
    if (chamadoIdParam) {
      setView('chamados');
      carregarMeusChamados(chamadoIdParam);
    } else if (viewParam === 'chamados') {
      setView('chamados');
      carregarMeusChamados();
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSessions = async () => {
    try {
      const res = await api.get('/nau/sessions');
      setSessions(res.data);
    } catch (err) { console.error('Erro ao carregar sessões:', err); }
    finally { setLoadingSessions(false); }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await api.get(`/nau/sessions/${sessionId}`);
      setActiveSession(res.data.session);
      setMessages(res.data.messages);
    } catch {
      toast.error('Erro ao carregar conversa');
    }
  };

  const handleNewChat = () => {
    setActiveSession(null);
    setMessages([]);
    setInput('');
    inputRef.current?.focus();
  };

  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/nau/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (activeSession?.id === sessionId) handleNewChat();
      toast.success('Conversa excluída');
    } catch {
      toast.error('Erro ao excluir');
    }
  };

  // ===== Meus Chamados =====
  const carregarMeusChamados = async (autoSelecionarId) => {
    setLoadingChamados(true);
    try {
      const res = await api.get('/tickets');
      setMeusChamados(res.data);
      if (autoSelecionarId) {
        const alvo = res.data.find(c => c.id === autoSelecionarId);
        if (alvo) abrirMeuChamado(alvo.id);
      }
    } catch {
      toast.error('Erro ao carregar seus chamados');
    } finally {
      setLoadingChamados(false);
    }
  };

  const abrirMeuChamado = async (chamadoId) => {
    try {
      const res = await api.get(`/tickets/${chamadoId}`);
      setChamadoSelecionado(res.data);
    } catch {
      toast.error('Erro ao abrir chamado');
    }
  };

  const handleSelecionarAnexoChamado = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Arquivo muito grande (máximo 8MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAnexoChamado({ file, preview: reader.result });
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const enviarRespostaChamado = async () => {
    if ((!respostaChamado.trim() && !anexoChamado) || !chamadoSelecionado) return;
    setEnviandoResposta(true);
    try {
      let anexos = [];
      if (anexoChamado) {
        const formData = new FormData();
        formData.append('file', anexoChamado.file);
        const up = await api.post(`/tickets/${chamadoSelecionado.id}/anexos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        anexos = [up.data];
      }
      const res = await api.post(`/tickets/${chamadoSelecionado.id}/mensagens`, {
        mensagem: respostaChamado.trim() || '(anexo enviado)',
        anexos,
      });
      setChamadoSelecionado(res.data);
      setRespostaChamado('');
      setAnexoChamado(null);
      carregarMeusChamados();
    } catch {
      toast.error('Erro ao enviar resposta');
    } finally {
      setEnviandoResposta(false);
    }
  };

  const trocarView = (novaView) => {
    setView(novaView);
    if (novaView === 'chamados' && meusChamados.length === 0) carregarMeusChamados();
  };


  const handleSelecionarImagem = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const tiposAceitos = ['image/png', 'image/jpeg', 'image/webp'];
    if (!tiposAceitos.includes(file.type)) {
      toast.error('Envie apenas imagens PNG, JPEG ou WEBP');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Imagem muito grande (máximo 8MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImagemBase64(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const sendMessage = async (text, opts = {}) => {
    const { forcarSuporte = false, forcarNegocio = false } = opts;
    if (!text?.trim() && !imagemBase64) return;
    const userMsg = text?.trim() || '(imagem anexada)';
    const imagemEnviada = imagemBase64;
    setInput('');
    setImagemBase64(null);
    setSending(true);

    const tempUserMsg = { id: 'temp-user', role: 'user', content: userMsg, anexo_preview: imagemEnviada, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const res = await api.post('/nau/chat', {
        message: userMsg,
        session_id: activeSession?.id || null,
        model,
        forcar_modo_suporte: forcarSuporte,
        forcar_modo_negocio: forcarNegocio,
        page_context: window.location.pathname,
        image_base64: imagemEnviada || undefined,
      });

      const { response, session_id, modo, sugerir_chamado, tipo_sugerido, anexo_url } = res.data;

      if (!activeSession) {
        const newSession = { id: session_id, title: userMsg.slice(0, 60), updated_at: new Date().toISOString() };
        setActiveSession(newSession);
        setSessions(prev => [newSession, ...prev]);
      }

      const assistantMsg = {
        id: res.data.message_id, role: 'assistant', content: response, model: res.data.model,
        modo, sugerir_chamado, tipo_sugerido, created_at: new Date().toISOString()
      };
      setMessages(prev => [
        ...prev.filter(m => m.id !== 'temp-user'),
        { ...tempUserMsg, id: `user-${Date.now()}`, anexo_url: anexo_url || undefined },
        assistantMsg
      ]);

    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== 'temp-user'));
      const detail = err.response?.data?.detail || 'Erro ao enviar mensagem';
      toast.error(detail);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const abrirDialogoChamado = (msg, idxAnterior) => {
    const mensagemUsuario = idxAnterior >= 0 ? messages[idxAnterior] : null;
    const anexoUrl = mensagemUsuario?.anexo_url;
    setChamadoContexto({
      msgId: msg.id,
      titulo: (mensagemUsuario?.content || 'Chamado aberto via Nau IA').slice(0, 70),
      mensagem: `Pergunta: ${mensagemUsuario?.content || ''}\n\nResposta do Nau IA: ${msg.content}`,
      anexos: anexoUrl ? [{ nome: 'print.png', url: anexoUrl, tipo: 'image/png', tamanho: 0 }] : [],
    });
    setChamadoTipo(msg.tipo_sugerido || 'duvida');
    setChamadoDialogOpen(true);
  };

  const confirmarAbrirChamado = async () => {
    if (!chamadoContexto) return;
    setCriandoChamado(true);
    try {
      await api.post('/tickets', {
        tipo: chamadoTipo,
        titulo: chamadoContexto.titulo,
        mensagem: chamadoContexto.mensagem,
        session_id: activeSession?.id || null,
        page_context: 'Nau IA - Modo Suporte',
        anexos: chamadoContexto.anexos || [],
      });
      setChamadosAbertos(prev => ({ ...prev, [chamadoContexto.msgId]: true }));
      toast.success('Chamado aberto! Nosso time vai responder por aqui mesmo, nesta conversa.');
      setChamadoDialogOpen(false);
    } catch {
      toast.error('Erro ao abrir chamado');
    } finally {
      setCriandoChamado(false);
    }
  };

  const enviarFeedback = async (msgId, tipo) => {
    if (feedbackDado[msgId] || !activeSession?.id) return;
    setFeedbackDado(prev => ({ ...prev, [msgId]: tipo }));
    try {
      await api.post('/nau/feedback', { message_id: msgId, session_id: activeSession.id, feedback: tipo });
      if (tipo === 'negativo') toast.info('Obrigado pelo retorno — abrimos um chamado para nossa equipe revisar esta resposta.');
    } catch {
      setFeedbackDado(prev => {
        const novo = { ...prev };
        delete novo[msgId];
        return novo;
      });
      toast.error('Erro ao registrar feedback');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const showWelcome = !activeSession && messages.length === 0;

  return (
    <div className="h-[calc(100vh-80px)] flex" data-testid="nau-copilot-page">
      {/* Sidebar */}
      <div className="w-72 border-r border-gray-200 bg-white flex flex-col" data-testid="nau-sidebar">
        <div className="p-3 border-b border-gray-100 flex gap-1.5">
          <button
            onClick={() => trocarView('chat')}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg transition-colors ${
              view === 'chat' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            data-testid="nau-tab-conversas"
          >
            <MessageSquare className="w-3.5 h-3.5" /> Conversas
          </button>
          <button
            onClick={() => trocarView('chamados')}
            className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg transition-colors ${
              view === 'chamados' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            data-testid="nau-tab-chamados"
          >
            <LifeBuoy className="w-3.5 h-3.5" /> Meus Chamados
          </button>
        </div>

        {view === 'chat' ? (
          <>
            <div className="p-3 border-b border-gray-100">
              <Button
                onClick={handleNewChat}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm"
                data-testid="new-chat-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Nova conversa
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingSessions ? (
                <div className="flex items-center justify-center h-20">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              ) : sessions.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">Nenhuma conversa ainda</p>
              ) : (
                sessions.map(s => (
                  <div
                    key={s.id}
                    className={`w-full text-left px-3 py-2.5 text-sm border-b border-gray-50 hover:bg-gray-50 transition-colors group flex items-center gap-2 cursor-pointer ${
                      activeSession?.id === s.id ? 'bg-blue-50 border-l-2 border-l-blue-600' : ''
                    }`}
                    onClick={() => loadSession(s.id)}
                    data-testid={`session-${s.id}`}
                  >
                    <MessageSquare className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate flex-1 text-gray-700">{s.title || 'Conversa'}</span>
                    <span
                      role="button"
                      className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-red-500 text-gray-400 transition-opacity cursor-pointer"
                      onClick={(e) => handleDeleteSession(s.id, e)}
                      data-testid={`delete-session-${s.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </span>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <>
            <div className="p-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">Chamados abertos por você</span>
              <button onClick={() => carregarMeusChamados()} data-testid="meus-chamados-refresh-btn" className="text-gray-400 hover:text-blue-600">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="meus-chamados-list">
              {loadingChamados ? (
                <div className="flex items-center justify-center h-20">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              ) : meusChamados.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Inbox className="w-7 h-7 mx-auto mb-2 text-gray-300" />
                  <p className="text-xs">Nenhum chamado aberto ainda</p>
                </div>
              ) : (
                meusChamados.map(c => (
                  <button
                    key={c.id}
                    onClick={() => abrirMeuChamado(c.id)}
                    className={`w-full text-left px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                      chamadoSelecionado?.id === c.id ? 'bg-blue-50 border-l-2 border-l-blue-600' : ''
                    }`}
                    data-testid={`meu-chamado-item-${c.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-800 truncate flex-1">{c.titulo}</span>
                      <Badge className={`text-[10px] ml-1 ${STATUS_COLOR[c.status]}`}>{STATUS_LABEL[c.status]}</Badge>
                    </div>
                    <Badge className={`text-[10px] ${TIPO_COLOR[c.tipo]}`}>{TIPO_LABEL[c.tipo]}</Badge>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Main */}
      {view === 'chamados' ? (
        <div className="flex-1 flex flex-col bg-gray-50/50">
          <input
            ref={fileInputChamadoRef}
            type="file"
            className="hidden"
            onChange={handleSelecionarAnexoChamado}
            data-testid="meu-chamado-anexo-input"
          />
          {!chamadoSelecionado ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm" data-testid="meu-chamado-vazio">
              Selecione um chamado à esquerda para ver o histórico
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-gray-200 bg-white flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-gray-900" data-testid="meu-chamado-titulo">{chamadoSelecionado.titulo}</p>
                  <p className="text-xs text-gray-400">Chamado aberto em {new Date(chamadoSelecionado.created_at).toLocaleDateString('pt-BR')}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge className={`text-[11px] ${TIPO_COLOR[chamadoSelecionado.tipo]}`}>{TIPO_LABEL[chamadoSelecionado.tipo]}</Badge>
                  <Badge className={`text-[11px] ${STATUS_COLOR[chamadoSelecionado.status]}`} data-testid="meu-chamado-status-badge">{STATUS_LABEL[chamadoSelecionado.status]}</Badge>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="meu-chamado-historico">
                {chamadoSelecionado.historico?.map((m) => (
                  <div key={m.id} className={`flex ${m.role === 'admin' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[75%] rounded-xl px-3.5 py-2.5 ${m.role === 'admin' ? 'bg-teal-600 text-white' : 'bg-blue-600 text-white'}`}>
                      <div className="flex items-center gap-1.5 mb-1 text-[11px] opacity-80">
                        {m.role === 'admin' ? <Headset className="w-3 h-3" /> : <UserIcon className="w-3 h-3" />}
                        {m.role === 'admin' ? `Suporte · ${m.autor_nome}` : 'Você'} · {new Date(m.created_at).toLocaleString('pt-BR')}
                      </div>
                      <p className="text-sm whitespace-pre-wrap break-words">{m.mensagem}</p>
                      {(m.anexos || []).map((a) => (
                        a.tipo?.startsWith('image/') ? (
                          <a key={a.id || a.url} href={a.url} target="_blank" rel="noreferrer">
                            <img src={a.url} alt={a.nome || 'anexo'} className="rounded-lg mt-2 max-h-40 border border-white/20" />
                          </a>
                        ) : (
                          <a key={a.id || a.url} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs mt-1.5 underline opacity-90">
                            <Paperclip className="w-3 h-3" /> {a.nome || 'anexo'}
                          </a>
                        )
                      ))}
                    </div>
                  </div>
                ))}
                <div ref={historicoEndRef} />
              </div>
              <div className="px-5 py-3 border-t border-gray-200 bg-white">
                {anexoChamado && (
                  <div className="relative inline-block mb-2" data-testid="meu-chamado-anexo-preview">
                    <img src={anexoChamado.preview} alt="Pré-visualização" className="h-14 rounded-lg border border-gray-200" />
                    <button type="button" onClick={() => setAnexoChamado(null)} className="absolute -top-1.5 -right-1.5 bg-gray-800 text-white rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputChamadoRef.current?.click()}
                    className="h-11 w-11 flex-shrink-0 rounded-lg border border-gray-200 text-gray-400 hover:text-blue-600 flex items-center justify-center"
                    data-testid="meu-chamado-anexar-btn"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <Textarea
                    value={respostaChamado}
                    onChange={(e) => setRespostaChamado(e.target.value)}
                    placeholder="Escreva uma mensagem para o suporte..."
                    className="min-h-[44px] max-h-32 text-sm"
                    data-testid="meu-chamado-resposta-textarea"
                  />
                  <Button
                    onClick={enviarRespostaChamado}
                    disabled={enviandoResposta || (!respostaChamado.trim() && !anexoChamado)}
                    className="h-11 bg-blue-600 hover:bg-blue-700"
                    data-testid="meu-chamado-enviar-btn"
                  >
                    {enviandoResposta ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
      <div className="flex-1 flex flex-col bg-gray-50/50">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleSelecionarImagem}
          data-testid="nau-imagem-input"
        />
        {showWelcome ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6" data-testid="nau-welcome">
            {/* Title with Help */}
            <div className="mb-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Anchor className="w-8 h-8 text-white" />
              </div>
              <TitleWithHelp
                title="Nau IA"
                description="Assistente inteligente do Nautilus OS. O Nau consulta dados reais do sistema (pipeline, propostas, clientes, fornecedores, projetos, produção, compras, estoque) para fornecer análises e recomendações estratégicas."
                connections={['Pipeline', 'Propostas', 'Clientes', 'Fornecedores', 'Projetos', 'Produção', 'Compras', 'Estoque']}
              >
                <h1 className="text-2xl font-bold text-gray-900 inline" data-testid="nau-welcome-title">
                  Olá, {primeiroNome}!
                </h1>
              </TitleWithHelp>
              <p className="text-gray-500 text-base mt-1">
                Sou o Nau, seu assistente inteligente do Nautilus OS
              </p>
            </div>

            {/* Menu Inicial (Quick Replies) */}
            <div className="w-full max-w-2xl grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-6" data-testid="nau-quick-replies-menu">
              {RESPOSTAS_RAPIDAS.map((r) => (
                <button
                  key={r.label}
                  onClick={() => sendMessage(r.texto, { forcarSuporte: r.forcarSuporte, forcarNegocio: r.forcarNegocio })}
                  className="text-sm text-center bg-white hover:bg-blue-50 text-gray-700 hover:text-blue-700 border border-gray-200 hover:border-blue-200 rounded-xl px-3 py-3 transition-colors shadow-sm"
                  data-testid={`nau-quick-reply-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="block text-lg mb-1">{r.emoji}</span>
                  {r.label}
                </button>
              ))}
            </div>

            {/* Input */}
            <div className="w-full max-w-2xl mb-10">
              {imagemBase64 && (
                <div className="relative inline-block mb-2" data-testid="nau-imagem-preview-welcome">
                  <img src={imagemBase64} alt="Pré-visualização" className="h-16 rounded-lg border border-gray-200" />
                  <button
                    type="button"
                    onClick={() => setImagemBase64(null)}
                    className="absolute -top-1.5 -right-1.5 bg-gray-800 text-white rounded-full p-0.5 hover:bg-gray-700"
                    data-testid="nau-remover-imagem-btn-welcome"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="relative bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 p-1.5 z-10"
                  title="Anexar print de erro"
                  data-testid="nau-anexar-imagem-btn-welcome"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="No que você está pensando hoje?"
                  className="border-0 h-14 text-base pl-11 pr-14 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                  disabled={sending}
                  data-testid="nau-main-input"
                />
                <Button
                  onClick={() => sendMessage(input)}
                  disabled={sending || (!input.trim() && !imagemBase64)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl h-10 w-10 p-0 bg-blue-600 hover:bg-blue-700"
                  data-testid="nau-send-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>

              <div className="flex items-center justify-center gap-2 mt-3">
                <button
                  className={`text-xs px-3 py-1 rounded-full transition-colors ${model === 'flash' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  onClick={() => setModel('flash')}
                  data-testid="model-flash-btn"
                >
                  <Zap className="w-3 h-3 inline mr-1" />Flash
                </button>
                <button
                  className={`text-xs px-3 py-1 rounded-full transition-colors ${model === 'advanced' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  onClick={() => setModel('advanced')}
                  data-testid="model-advanced-btn"
                >
                  <Sparkles className="w-3 h-3 inline mr-1" />Avançado
                </button>
              </div>
            </div>

            {/* Suggestions */}
            <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="nau-suggestions">
              {PROMPT_SUGGESTIONS.map(group => {
                const Icon = group.icon;
                return (
                  <div key={group.area} className={`rounded-xl border p-4 ${group.color}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-semibold">{group.area}</span>
                    </div>
                    <div className="space-y-1.5">
                      {group.prompts.map((prompt, i) => (
                        <button
                          key={`${group.area}-${prompt.slice(0,20)}-${i}`}
                          className="w-full text-left text-xs text-gray-700 hover:text-gray-900 bg-white/60 hover:bg-white rounded-lg px-3 py-2 transition-colors flex items-center gap-2 group"
                          onClick={() => sendMessage(prompt)}
                          data-testid={`suggestion-${group.area.toLowerCase()}-${i}`}
                        >
                          <span className="flex-1 line-clamp-1">{prompt}</span>
                          <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="px-5 py-3 border-b border-gray-200 bg-white flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <Anchor className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <TitleWithHelp
                  title="Nau IA"
                  description="Assistente inteligente do Nautilus OS. Consulta dados reais de todo o sistema para análises e recomendações."
                  connections={['Pipeline', 'Propostas', 'Clientes', 'Fornecedores', 'Projetos', 'Produção']}
                >
                  <p className="text-sm font-medium text-gray-900">Nau</p>
                </TitleWithHelp>
                <p className="text-[11px] text-gray-400">
                  {model === 'advanced' ? 'Modo Avançado (GPT-5.2)' : 'Modo Rápido (Gemini Flash)'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className={`text-xs px-3 py-1 rounded-full transition-colors ${model === 'flash' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  onClick={() => setModel('flash')}
                >
                  <Zap className="w-3 h-3 inline mr-1" />Flash
                </button>
                <button
                  className={`text-xs px-3 py-1 rounded-full transition-colors ${model === 'advanced' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  onClick={() => setModel('advanced')}
                >
                  <Sparkles className="w-3 h-3 inline mr-1" />Avançado
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="nau-messages">
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
                  data-testid={`nau-msg-${idx}`}
                >
                  {msg.role === 'assistant' && msg.modo === 'suporte' && (
                    <span className="ml-9 mb-1 inline-flex items-center gap-1 text-[11px] font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5" data-testid={`nau-modo-suporte-badge-${idx}`}>
                      <Headset className="w-3 h-3" /> Modo Suporte
                    </span>
                  )}
                  <div className={`flex w-full ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center mr-2 mt-1 flex-shrink-0">
                        <Anchor className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-gray-200 text-gray-800 shadow-sm'
                      }`}
                    >
                      {(msg.anexo_preview || msg.anexo_url) && (
                        <img
                          src={msg.anexo_preview || msg.anexo_url}
                          alt="Print anexado"
                          className="rounded-lg mb-2 max-h-48 border border-white/20"
                          data-testid={`nau-msg-anexo-${idx}`}
                        />
                      )}
                      <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                        {msg.role === 'assistant' ? (
                          <ReactMarkdown
                            components={{
                              p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                              strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                              ul: ({children}) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
                              ol: ({children}) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                              li: ({children}) => <li className="text-sm">{children}</li>,
                              h3: ({children}) => <h3 className="font-bold text-sm mt-3 mb-1">{children}</h3>,
                              h4: ({children}) => <h4 className="font-semibold text-sm mt-2 mb-1">{children}</h4>,
                              code: ({children}) => <code className="bg-gray-100 px-1 rounded text-xs">{children}</code>,
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        ) : msg.content}
                      </div>
                    </div>
                  </div>
                  {msg.role === 'assistant' && (
                    <div className="ml-9 mt-1 flex items-center gap-1">
                      <button
                        onClick={() => enviarFeedback(msg.id, 'positivo')}
                        disabled={!!feedbackDado[msg.id]}
                        className={`p-1 rounded-lg transition-colors ${feedbackDado[msg.id] === 'positivo' ? 'text-emerald-600 bg-emerald-50' : 'text-gray-300 hover:text-emerald-600'}`}
                        data-testid={`nau-feedback-positivo-${idx}`}
                        title="Resposta útil"
                      >
                        <ThumbsUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => enviarFeedback(msg.id, 'negativo')}
                        disabled={!!feedbackDado[msg.id]}
                        className={`p-1 rounded-lg transition-colors ${feedbackDado[msg.id] === 'negativo' ? 'text-red-600 bg-red-50' : 'text-gray-300 hover:text-red-600'}`}
                        data-testid={`nau-feedback-negativo-${idx}`}
                        title="Resposta não ajudou"
                      >
                        <ThumbsDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {msg.role === 'assistant' && msg.sugerir_chamado && (
                    <div className="ml-9 mt-1.5">
                      {chamadosAbertos[msg.id] ? (
                        <span className="text-xs text-emerald-600 font-medium">✓ Chamado aberto — acompanhe a resposta aqui mesmo</span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={() => abrirDialogoChamado(msg, idx - 1)}
                          data-testid={`nau-abrir-chamado-btn-${idx}`}
                        >
                          <LifeBuoy className="w-3 h-3 mr-1" /> Abrir Chamado
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center">
                    <Anchor className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Nau está analisando...
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-5 py-3 border-t border-gray-200 bg-white">
              <div className="max-w-4xl mx-auto">
                {imagemBase64 && (
                  <div className="relative inline-block mb-2" data-testid="nau-imagem-preview">
                    <img src={imagemBase64} alt="Pré-visualização" className="h-16 rounded-lg border border-gray-200" />
                    <button
                      type="button"
                      onClick={() => setImagemBase64(null)}
                      className="absolute -top-1.5 -right-1.5 bg-gray-800 text-white rounded-full p-0.5 hover:bg-gray-700"
                      data-testid="nau-remover-imagem-btn"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 p-1.5"
                    title="Anexar print de erro"
                    data-testid="nau-anexar-imagem-btn"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Pergunte algo ao Nau..."
                    className="h-12 pl-10 pr-14 rounded-xl border-gray-200 text-sm"
                    disabled={sending}
                    data-testid="nau-chat-input"
                  />
                  <Button
                    onClick={() => sendMessage(input)}
                    disabled={sending || (!input.trim() && !imagemBase64)}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-lg h-9 w-9 p-0 bg-blue-600 hover:bg-blue-700"
                    data-testid="nau-chat-send-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {/* Dialog: Abrir Chamado */}
      <Dialog open={chamadoDialogOpen} onOpenChange={setChamadoDialogOpen}>
        <DialogContent data-testid="nau-chamado-dialog">
          <DialogHeader>
            <DialogTitle>Abrir Chamado de Suporte</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              O Nau não conseguiu resolver totalmente sua dúvida. Vamos encaminhar esta conversa para o time de
              suporte — a resposta aparece aqui mesmo, nesta conversa.
            </p>
            <div>
              <Label>Tipo de Chamado</Label>
              <p className="text-[11px] text-teal-600 mb-1">Classificação sugerida automaticamente pelo Nau — ajuste se necessário.</p>
              <Select value={chamadoTipo} onValueChange={setChamadoTipo}>
                <SelectTrigger data-testid="nau-chamado-tipo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_CHAMADO.map(t => (
                    <SelectItem key={t.valor} value={t.valor}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {chamadoContexto?.anexos?.length > 0 && (
                <p className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1">
                  <Paperclip className="w-3 h-3" /> O print anexado nesta conversa será enviado junto com o chamado.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChamadoDialogOpen(false)} data-testid="nau-chamado-cancelar-btn">
              Cancelar
            </Button>
            <Button onClick={confirmarAbrirChamado} disabled={criandoChamado} data-testid="nau-chamado-confirmar-btn">
              {criandoChamado ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <LifeBuoy className="w-4 h-4 mr-1" />}
              Abrir Chamado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
